import { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Avatar,
  Box,
  Switch,
  ListItem,
  List,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  ListItemText,
  AppBar,
  CircularProgress,
  Toolbar
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from "@mui/icons-material/Close";
import { useCRMApi } from "../../../../../infra/crm-api/crmApi";
import { useAppSelector } from "../../../../../application/states/hooks";
import { darkenColor } from "../../../../../utils";
import { theme } from "../../../../styles";
import type { CRMConfig, FunnelSdrConfig } from "../../../../../infra/crm-api/types";
import { useDispatch } from "react-redux";
import { executeAlert } from "../../../../../application/states/toast-alert";

const CRM_OPTIONS = [
  { value: "hubspot", label: "Hubspot", icon: "/icons/hubspot.png" },
  { value: "rdstation", label: "RD Station", icon: "/icons/rdstation.png" },
  { value: "Salesforce", label: "Salesforce", icon: "/icons/Salesforce.png" },
  { value: "zoho", label: "zoho", icon: "/icons/zoho.png" },
  { value: "ExactSpotter", label: "ExactSpotter", icon: "/icons/exactsales.png" },
  { value: "Bitrix24", label: "Bitrix24", icon: "/icons/bitrix24.jpg" }
];

export const IntegrationSection = () => {
  const { company } = useAppSelector((state) => state.company);
  const [configs, setConfigs] = useState<CRMConfig[]>([]);
  const [token, setToken] = useState("");
  const [bitrixDomain, setBitrixDomain] = useState("");
  const [selectedCRM, setSelectedCRM] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const {
    listCRMConfigs,
    getCRMConfigById,
    createCRMConfig,
    updateCRMConfig,
    listFunnelConfigs,
    createFunnelConfig,
    updateFunnelConfig
  } = useCRMApi();
  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<CRMConfig | null>(null);
  const [funnelConfigs, setFunnelConfigs] = useState<FunnelSdrConfig[]>([]);

  const funnelItems = funnelConfigs[0]?.funnelItems ?? [];
  const sdrItems = funnelConfigs[0]?.sdrItems ?? [];
  const [newFunnel, setNewFunnel] = useState("");
  const [newSdr, setNewSdr] = useState("");
  const [newSdrBitrixId, setNewSdrBitrixId] = useState("");


  const [newFunnelError, setNewFunnelError] = useState<string | null>(null);

  const [newSdrError, setNewSdrError] = useState<string | null>(null);

  const [initialFunnelIds, setInitialFunnelIds] = useState<string[]>([]);
  const [initialSdrIds, setInitialSdrIds] = useState<string[]>([]);

  const [isDirty, setIsDirty] = useState(false);
  const [initialFunnelState, setInitialFunnelState] = useState<Record<string, boolean>>({});
  const [initialSdrState, setInitialSdrState] = useState<Record<string, boolean>>({});

  const [saving, setSaving] = useState(false);

  const [initialCRMName, setInitialCRMName] = useState("");
  const [initialToken, setInitialToken] = useState("");
  const [initialBitrixDomain, setInitialBitrixDomain] = useState("");


  // logo no topo do seu componente, após todos os useState:
  const availableCrmOptions = CRM_OPTIONS.filter(opt => {
    const alreadyActive = configs.some(cfg => cfg.name === opt.value && cfg.active);
    // se for edição, sempre deixamos a opção do CRM que estamos editando
    if (isEdit) {
      return opt.value === editingConfig?.name || !alreadyActive;
    }
    // se for criação, só mostramos CRMs que ainda não estão ativos
    return !alreadyActive;
  });


  const toggleFunnelActive = (key: string, newActive: boolean) => {
    setFunnelConfigs(prev =>
      prev.map(cfg => ({
        ...cfg,
        funnelItems: cfg.funnelItems.map(f => {
          const isMatch = f.id
            ? f.id === key
            : f.funnelId === key;
          return isMatch
            ? { ...f, active: newActive }
            : f;
        })
      }))
    );
    setIsDirty(true);
  };
  const toggleSdrActive = (key: string, newActive: boolean) => {
    setFunnelConfigs(prev =>
      prev.map(cfg => ({
        ...cfg,
        sdrItems: cfg.sdrItems.map(s => {
          const isMatch = s.id
            ? s.id === key
            : s.email === key;
          return isMatch
            ? { ...s, active: newActive }
            : s;
        })
      }))
    );
    setIsDirty(true);
  };


  useEffect(() => {
    if (company?.id) {
      listCRMConfigs(company.id).then(setConfigs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const emptyFc: FunnelSdrConfig = {
    id: "",
    crmConfigId: editingConfig?.id ?? "",
    funnelItems: [],
    sdrItems: [],
    createdAt: "",
    updatedAt: ""
  };


  const handleOpenModal = async (cfg?: CRMConfig) => {
    if (cfg) {
      setIsEdit(true);
      // Define valores iniciais com os dados disponíveis enquanto carrega
      setEditingConfig(cfg);
      setSelectedCRM(cfg.name || '');
      setToken(''); // Será preenchido quando carregar
      setOpenModal(true);

      try {
        // Busca a config completa com token para edição
        const fullConfig = await getCRMConfigById(cfg.id);
        if (!fullConfig) {
          dispatch(executeAlert({
            message: 'Configuração não encontrada.',
            type: 'error'
          }));
          return;
        }
        setEditingConfig(fullConfig);
        setToken(fullConfig.token || '');
        setBitrixDomain(fullConfig.bitrixDomain || '');
        setInitialCRMName(fullConfig.name || '');
        setInitialToken(fullConfig.token || '');
        setInitialBitrixDomain(fullConfig.bitrixDomain || '');

        const list = await listFunnelConfigs(fullConfig.id);
        setFunnelConfigs(list);

        const funnelMap = Object.fromEntries(list[0]?.funnelItems.map(f => [f.id, f.active]) ?? []);
        const sdrMap = Object.fromEntries(list[0]?.sdrItems.map(s => [s.id, s.active]) ?? []);
        setInitialFunnelState(funnelMap);
        setInitialSdrState(sdrMap);

        // snapshot dos IDs iniciais
        setInitialFunnelIds(list[0]?.funnelItems.map(f => f.id) ?? []);
        setInitialSdrIds(list[0]?.sdrItems.map(s => s.id) ?? []);
      } catch (error: any) {
        dispatch(executeAlert({
          message: error.response?.data?.result?.error || error.message || 'Erro ao carregar configuração.',
          type: 'error'
        }));
      }
    } else {
      setOpenModal(true);
    }
  };

  // 4) detector de dirty sempre que funnelItems ou sdrItems mudam:
  useEffect(() => {
    if (!isEdit) {
      setIsDirty(true);
      return;
    }
    // IDs e novos
    const currentFunnelIds = funnelItems.map(f => f.id);
    const currentSdrIds = sdrItems.map(s => s.id);
    const hasNewFunnel = funnelItems.some(f => f.id === "");
    const hasNewSdr = sdrItems.some(s => s.id === "");
    const funnelIdsDiffer = currentFunnelIds.sort().join(",") !== initialFunnelIds.sort().join(",");
    const sdrIdsDiffer = currentSdrIds.sort().join(",") !== initialSdrIds.sort().join(",");
    const hasNameChanged = selectedCRM !== initialCRMName;
    const hasTokenChanged = token !== initialToken;
    // **ACTIVE DIFERENTES**  
    const funnelActiveDiffer = funnelItems.some(f => f.id && initialFunnelState[f.id] !== f.active);
    const sdrActiveDiffer = sdrItems.some(s => s.id && initialSdrState[s.id] !== s.active);
    const hasBitrixDomainChanged = bitrixDomain !== initialBitrixDomain;

    setIsDirty(
      hasNewFunnel ||
      hasNewSdr ||
      funnelIdsDiffer ||
      sdrIdsDiffer ||
      funnelActiveDiffer ||
      sdrActiveDiffer ||
      hasNameChanged ||
      hasTokenChanged ||
      hasBitrixDomainChanged
    );
  }, [
    funnelItems,
    sdrItems,
    initialFunnelIds,
    initialSdrIds,
    initialFunnelState,
    initialSdrState,
    isEdit,
    initialCRMName,
    initialToken,
    selectedCRM,
    token,
    bitrixDomain,
    initialBitrixDomain
  ]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsEdit(false);
    setEditingConfig(null);
    setSelectedCRM("");
    setToken("");
    setBitrixDomain("");
    setFunnelConfigs([]);
    setNewFunnel("");
    setNewSdr("");
    setNewSdrBitrixId("");
  };

  const addFunnel = () => {
    const trimmed = newFunnel.trim();
    if (!trimmed) return;
    // impede duplicata de vez
    if (funnelItems.some(f => f.funnelId === trimmed)) {
      dispatch(executeAlert({
        message: 'Não pode adicionar FunnelId duplicado.',
        type: 'error'
      }));
      return;
    }
    const base = funnelConfigs[0] || emptyFc;
    setFunnelConfigs([{
      ...base,
      funnelItems: [...base.funnelItems, { id: '', funnelId: trimmed, active: true }]
    }]);
    setNewFunnel("");
  };

  const addSdr = () => {
    const trimmed = newSdr.trim();
    if (!trimmed) return;
    if (selectedCRM === "Bitrix24" && !newSdrBitrixId.trim()) return;
    if (sdrItems.some(s => s.email === trimmed)) {
      dispatch(executeAlert({
        message: 'Não pode adicionar e-mail SDR duplicado.',
        type: 'error'
      }));
      return;
    }
    const base = funnelConfigs[0] || emptyFc;
    setFunnelConfigs([{
      ...base,
      sdrItems: [...base.sdrItems, { id: '', email: trimmed, active: true, bitrixUserId: newSdrBitrixId.trim() || undefined }]
    }]);
    setNewSdr("");
    setNewSdrBitrixId("");
  };

  const handleSaveAll = async () => {
    // 1) Deve ter pelo menos um funnelId
    try {

      // 0) CRM e token são obrigatórios
      if (!selectedCRM) {
        dispatch(executeAlert({
          message: 'Você precisa selecionar um CRM.',
          type: 'error'
        }));
        return;
      }
      if (!token.trim()) {
        dispatch(executeAlert({
          message: 'Você precisa informar o token de integração.',
          type: 'error'
        }));
        return;
      }


      let finalFunnelItems = [...funnelItems];
      if (selectedCRM !== "Bitrix24" && newFunnel.trim()) {
        const trimmed = newFunnel.trim();
        if (!finalFunnelItems.some(f => f.funnelId === trimmed)) {
          finalFunnelItems.push({ id: '', funnelId: trimmed, active: true });
        }
      }

      let finalSdrItems = [...sdrItems];
      if (newSdr.trim()) {
        if (selectedCRM === "Bitrix24" && !newSdrBitrixId.trim()) {
          dispatch(executeAlert({
            message: 'Para adicionar esse SDR no Bitrix24, preencha também o ID dele ao lado e clique em [+].',
            type: 'error'
          }));
          return;
        }
        const trimmed = newSdr.trim();
        const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
        if (ok && !finalSdrItems.some(s => s.email === trimmed)) {
          finalSdrItems.push({ id: '', email: trimmed, active: true, bitrixUserId: newSdrBitrixId.trim() || undefined });
        } else if (!ok) {
          dispatch(executeAlert({
            message: 'O e-mail de SDR que você digitou é inválido.',
            type: 'error'
          }));
          return;
        }
      }

      if (selectedCRM !== "Bitrix24" && finalFunnelItems.length === 0) {
        dispatch(executeAlert({
          message: 'Você precisa adicionar ao menos um FunnelId. Digite e clique no [+].',
          type: 'error'
        }));
        return;
      }

      if (finalSdrItems.length === 0) {
        dispatch(executeAlert({
          message: 'Você precisa adicionar ao menos um SDR Email. Digite e clique no [+].',
          type: 'error'
        }));
        return;
      }
      
      // 2) Todos os funnelIds devem ser não-vazios (exceto Bitrix)
      if (selectedCRM !== "Bitrix24" && finalFunnelItems.some(f => !f.funnelId.trim())) {
        dispatch(executeAlert({
          message: 'Cada FunnelId precisa ser preenchido.',
          type: 'error'
        }));
        return;
      }

      // 3) Todos os SDR emails devem ser válidos
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (finalSdrItems.some(s => !emailRegex.test(s.email))) {
        dispatch(executeAlert({
          message: 'Um ou mais e-mails de SDR estão em formato inválido.',
          type: 'error'
        }));
        return;
      }

      // 4) Se for Bitrix24, todos os SDRs devem ter bitrixUserId
      if (selectedCRM === "Bitrix24" && finalSdrItems.some(s => !s.bitrixUserId || !s.bitrixUserId.trim())) {
        dispatch(executeAlert({
          message: 'Para o Bitrix24, todos os e-mails SDR adicionados na lista devem ter o ID preenchido.',
          type: 'error'
        }));
        return;
      }


      setSaving(true);
      if (isEdit && !editingConfig) {
        dispatch(executeAlert({
          message: 'Erro: configuração não encontrada.',
          type: 'error'
        }));
        setSaving(false);
        return;
      }
      let cfg = isEdit
        ? await updateCRMConfig({
          id: editingConfig!.id,
          companyId: company!.id,
          name: selectedCRM,
          token,
          bitrixDomain,
          active: editingConfig!.active,
        })
        : await createCRMConfig({
          companyId: company!.id,
          token,
          bitrixDomain,
          name: selectedCRM,
        });

      // 2) garante um FunnelSdrConfig existente (criando se necessário)
      let fcId = funnelConfigs[0]?.id;
      if (!isEdit || !fcId) {
        const createdFc = await createFunnelConfig({
          crmConfigId: cfg.id,
          funnelItems: finalFunnelItems.map(f => ({ funnelId: f.funnelId, active: f.active })),
          sdrItems: finalSdrItems.map(s => ({ email: s.email, active: s.active, bitrixUserId: s.bitrixUserId })),
        });
        fcId = createdFc.id;
        // recarrega do servidor o array completo
        setFunnelConfigs([createdFc]);
      } else {
        // 3) para cada item (tanto _update_ quanto _push_)
        for (const f of finalFunnelItems) {
          await updateFunnelConfig({
            id: fcId,
            funnelItem: {
              ...(f.id ? { id: f.id } : {}),   // só inclui o id se ele existir
              funnelId: f.funnelId,
              active: f.active,
            }
          });
        }
        for (const s of finalSdrItems) {
          await updateFunnelConfig({
            id: fcId,
            sdrItem: {
              ...(s.id ? { id: s.id } : {}),
              email: s.email,
              active: s.active,
              bitrixUserId: s.bitrixUserId
            }
          });
        }
      }

      // 4) recarrega tudo e fecha modal
      setConfigs(await listCRMConfigs(company!.id));
      handleCloseModal();
      dispatch(executeAlert({
        message: isEdit ? "Atualizado com sucesso" : "Criado com sucesso",
        type: "success"
      }));
    } catch (err) {
      dispatch(executeAlert({
        message: "erro interno ",
        type: "error"
      }));

    } finally {
      setSaving(false);
    }
  }

  const toggleActive = async (cfg: CRMConfig, newActive: boolean) => {
    if (newActive && configs.some(c => c.name === cfg.name && c.id !== cfg.id && c.active)) {
      return dispatch(executeAlert({ message: `Já existe uma integração ativa de ${cfg.name}.`, type: "error" }));
    }
    await updateCRMConfig({ id: cfg.id, companyId: cfg.companyId, name: cfg.name, token: cfg.token, active: newActive });
    setConfigs(await listCRMConfigs(cfg.companyId));
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6">Configurações Existentes</Typography>
          <List>
            {configs.map(cfg => (
              <Box key={cfg.id}>
                <ListItem secondaryAction={<Switch checked={cfg.active} onChange={(_, checked) => toggleActive(cfg, checked)} />}>
                  <Avatar src={CRM_OPTIONS.find(o => o.value === cfg.name)?.icon} sx={{ mr: 2 }} />
                  <Typography>{cfg.name}</Typography>
                  <IconButton onClick={() => handleOpenModal(cfg)}><EditIcon /></IconButton>
                </ListItem>
                <Divider />
              </Box>
            ))}
          </List>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" fullWidth onClick={() => handleOpenModal()}>
            Nova Integração
          </Button>
        </Grid>
      </Grid>

      <Dialog fullScreen open={openModal} onClose={handleCloseModal}>
        <AppBar position="relative">
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleCloseModal}><CloseIcon /></IconButton>
            <Typography sx={{ ml: 2 }} variant="h6">
              {isEdit && editingConfig ? `Editar ${editingConfig.name}` : "Nova Integração"}
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent>
          <Grid container spacing={2} mt={2}>
            {/* CRM */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Nome do CRM</InputLabel>
                <Select value={selectedCRM} onChange={e => setSelectedCRM(e.target.value)} label="Nome do CRM">
                  {availableCrmOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={opt.icon} sx={{ width: 24, height: 24 }} /> {opt.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Token */}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Token de Integração" value={token} onChange={e => setToken(e.target.value)} />
            </Grid>

            {/* Funis ou Domínio (Bitrix24) */}
            {selectedCRM === "Bitrix24" ? (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Domínio Bitrix24" value={bitrixDomain} onChange={e => setBitrixDomain(e.target.value)} placeholder="https://seudominio.bitrix24.com.br" />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                  <TextField
                    fullWidth
                    label="Novo FunnelId"
                    value={newFunnel}
                    error={!!newFunnelError}
                    helperText={newFunnelError ?? ""}
                    onChange={e => {
                      const v = e.target.value;
                      const trimmed = v.trim();
                      // Se já existe um funnelId exatamente igual, marca erro
                      if (trimmed && funnelItems.some(f => f.funnelId === trimmed)) {
                        setNewFunnelError("Este FunnelId já existe.");
                      } else {
                        setNewFunnelError(trimmed ? null : "FunnelId não pode ficar vazio");
                      }
                      setNewFunnel(v);
                    }}
                    onKeyDown={e => e.key === 'Enter' && !newFunnelError && addFunnel()}
                  />
                  <Button
                    onClick={addFunnel}
                    disabled={!!newFunnelError || !newFunnel.trim()}
                    sx={{ minWidth: '48px', height: '56px' }}
                  >➕</Button>
                </Box>
                <List dense>
                  {funnelItems.map(f => (
                    <ListItem key={f.id || f.funnelId}
                      secondaryAction={
                        <Switch
                          checked={f.active}
                          onChange={() => toggleFunnelActive(f.id || f.funnelId, !f.active)}
                        />
                      }
                    >
                      <ListItemText primary={f.funnelId} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}
            {/* SDRs */}
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                <TextField
                  fullWidth
                  label="Novo SDR Email"
                  value={newSdr}
                  error={!!newSdrError}
                  helperText={newSdrError ?? ""}
                  onChange={e => {
                    const v = e.target.value;
                    const trimmed = v.trim();
                    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
                    if (!ok) {
                      setNewSdrError("Formato de e-mail inválido");
                    } else if (sdrItems.some(s => s.email === trimmed)) {
                      setNewSdrError("Este e-mail já existe.");
                    } else {
                      setNewSdrError(null);
                    }
                    setNewSdr(v);
                  }}
                  onKeyDown={e => e.key === 'Enter' && !newSdrError && addSdr()}
                />
                {selectedCRM === "Bitrix24" && (
                  <TextField
                    fullWidth
                    label="ID do SDR (Bitrix24)"
                    value={newSdrBitrixId}
                    onChange={e => setNewSdrBitrixId(e.target.value)}
                    placeholder="Ex: 14"
                  />
                )}
                <Button 
                  onClick={addSdr} 
                  disabled={!!newSdrError || !newSdr.trim() || (selectedCRM === "Bitrix24" && !newSdrBitrixId.trim())}
                  sx={{ minWidth: '48px', height: '56px' }}
                >
                  ➕
                </Button>
              </Box>
              <List dense>
                {sdrItems.map(s => (
                  <ListItem key={s.id || s.email}
                    secondaryAction={
                      <Switch
                        checked={s.active}
                        // antes: onChange={() => toggleSdrActive(s.id, !s.active)}
                        onChange={() => toggleSdrActive(s.id || s.email, !s.active)}
                      />
                    }
                  >
                    <ListItemText
                      primary={s.email}
                      secondary={selectedCRM === "Bitrix24" && s.bitrixUserId ? `ID Bitrix: ${s.bitrixUserId}` : undefined}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveAll}
            disabled={(isEdit && !isDirty) || saving}
            startIcon={
              saving
                ? <CircularProgress size={20} color="inherit" />
                : undefined
            }
          >
            {saving
              ? isEdit ? "Atualizando…" : "Salvando…"
              : isEdit ? "Atualizar" : "Salvar"
            }
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
